import {
  ImportDetailDTO,
  ImportDetailStatusName,
} from '../interfaces/Import/ShipmentBulkUploadApi'
import { resolveDetailStatusName } from './shipmentBulkUploadStatus'
import { printFieldsFromPayload } from './shipmentPayload'

/**
 * Lógica pura de la impresión por selección en el detalle de carga masiva.
 *
 * Vive acá y no dentro del componente por la misma razón que `shipmentSelection`: la
 * pantalla está detrás de un login de dos pasos y su tabla se refresca sola cada ~2 s
 * por el polling de `useBulkUploadProgress`, así que no hay forma práctica de
 * ejercitar estas reglas desde la UI. Acá se testean sin navegador ni sesión.
 */

/** Fila de la carga que SÍ se puede imprimir: existe la guía y tiene número. */
export interface BulkPrintRow {
  /** `shipment_bulk_upload_detail_id`. Es la clave de selección, no el nº de guía:
   *  el polling reemplaza los objetos de `details`, pero los ids se mantienen. */
  detailId: string
  shipmentNumber: string
  /**
   * Solo lo consume Documento Bolsillo (`.NET DownloadAllDocumentDeclared`). Sale del
   * payload, con el mismo fallback `'60'` que ya aplican `ShipmentMultiPrintModal` y
   * la impresión de consulta.
   */
  serviceCode: string
  /**
   * Ídem, con fallback `''`. Puede quedar vacío legítimamente (ver
   * `printFieldsFromPayload`): las etiquetas Zebra NO dependen de esto, lo resuelve
   * Django desde la base.
   */
  buCodeSource: string
}

/** Fallbacks heredados de consulta — se replican para no cambiar el comportamiento. */
const FALLBACK_SERVICE_CODE = '60'
const FALLBACK_BU_CODE_SOURCE = ''

/**
 * Filas imprimibles de una carga.
 *
 * Usa EL MISMO predicado que `executedGuideNumbers` (`Executed` + `shipment_number` no
 * vacío) a propósito: el detalle deriva `bulkGuides` de acá, así que el manifiesto, el
 * aviso de tandas y la impresión no pueden divergir. Una guía sin número no se puede
 * preseleccionar ni imprimir, así que no aparece.
 */
export const printableRowsFromDetails = (
  details: ImportDetailDTO[] | null | undefined
): BulkPrintRow[] => {
  if (!Array.isArray(details)) return []

  return details.flatMap((d) => {
    if (!d) return []
    if (
      resolveDetailStatusName(d.status_name, d.status_id) !==
      ImportDetailStatusName.Executed
    )
      return []

    const shipmentNumber = d.shipment_number?.trim()
    if (!shipmentNumber) return []

    const { serviceCode, originStore } = printFieldsFromPayload(d.payload_json)

    return [
      {
        detailId: d.shipment_bulk_upload_detail_id,
        shipmentNumber,
        serviceCode: serviceCode ?? FALLBACK_SERVICE_CODE,
        buCodeSource: originStore ?? FALLBACK_BU_CODE_SOURCE,
      },
    ]
  })
}

/**
 * Descarta los ids seleccionados cuyas filas ya no existen.
 *
 * Hace falta porque el detalle se recarga solo: si el operador borra una fila (o el
 * backend la reemplaza) mientras estaba tildada, su id quedaría en la selección para
 * siempre y el contador diría "3 guías seleccionadas" con solo 2 imprimibles.
 */
export const prunedSelection = (
  selectedIds: string[],
  rows: BulkPrintRow[]
): string[] => {
  const alive = new Set(rows.map((r) => r.detailId))
  return selectedIds.filter((id) => alive.has(id))
}

/**
 * Filas seleccionadas, en el ORDEN DE LA TABLA y no en el de tildado.
 *
 * Importa para Zebra: las etiquetas salen de la impresora en el orden en que se
 * recorren, y el operador espera el mismo orden que ve en pantalla.
 */
export const selectedRows = (
  rows: BulkPrintRow[],
  selectedIds: string[]
): BulkPrintRow[] => {
  const wanted = new Set(selectedIds)
  return rows.filter((r) => wanted.has(r.detailId))
}

/**
 * Parte una selección en grupos de a lo sumo `size`.
 *
 * Es el reparto que usan LOS CINCO formatos: los PDF y el bolsillo lo recorren desde el
 * visor con Anterior/Siguiente, y Zebra lo recorre imprimiendo un grupo por vez y
 * esperando la respuesta antes de ofrecer el siguiente.
 *
 * Ninguno de los tres caminos aguanta la selección entera: los PDF chocan con el timeout
 * de 30 s de `apiDjango`, el bolsillo manda todo en una sola petición .NET, y Zebra hace
 * una petición por guía contra el agente local.
 *
 * Una selección vacía devuelve `[]`, no `[[]]`: "cero grupos", no "un grupo vacío".
 */
export const chunkPrintRows = (
  rows: BulkPrintRow[],
  size: number
): BulkPrintRow[][] => {
  const safeSize = Number.isFinite(size) && size > 0 ? Math.floor(size) : 1
  const groups: BulkPrintRow[][] = []
  for (let i = 0; i < rows.length; i += safeSize) {
    groups.push(rows.slice(i, i + safeSize))
  }
  return groups
}

/**
 * Próximo tamaño de grupo después de que el servicio se quedara sin tiempo.
 *
 * El tope inicial es una apuesta razonable, no una garantía: cuánto aguanta el backend
 * depende de su carga y de cuántas piezas tenga cada guía. Ante un timeout se baja de
 * una vez al piso en lugar de ir tanteando —cada tanteo le cuesta al operador otros 30 s
 * de espera— y si YA estaba en el piso se devuelve `null`: ahí el tamaño del grupo dejó
 * de ser la explicación y hay que escalar el problema.
 */
export const degradeBatchSize = (
  current: number,
  floor: number
): number | null => {
  const safeFloor = Number.isFinite(floor) && floor > 0 ? Math.floor(floor) : 1
  if (!Number.isFinite(current) || current <= safeFloor) return null
  return safeFloor
}

// ---------------------------------------------------------------------------
// Runner del bucle Zebra
// ---------------------------------------------------------------------------

/** Por qué falló una guía. `agent-down` corta el bucle; el resto no. */
export type ZebraFailureReason =
  | 'label-fetch'
  | 'agent-down'
  | 'print-failed'

export interface ZebraFailure {
  shipmentNumber: string
  reason: ZebraFailureReason
  message: string
}

export interface ZebraRunResult {
  printed: string[]
  failures: ZebraFailure[]
  /** Se cortó antes de terminar (cancelación, desmontaje o agente caído). */
  aborted: boolean
  abortReason?: 'cancelled' | 'unmounted' | 'agent-down'
}

export interface ZebraRunDeps {
  /** Trae el ZPL de una guía (Django resuelve servicio y oficina desde la base). */
  fetchLabel: (
    shipmentNumber: string
  ) => Promise<{
    ok: boolean
    stringLabel?: string[]
    labelFormat?: string
    message?: string
  }>
  /** Manda el ZPL al agente de impresión local. */
  sendToPrinter: (
    stringLabel: string[],
    labelFormat: string
  ) => Promise<{ ok: boolean; reason?: 'agent-down' | 'print-failed'; message?: string }>
  /** `true` cuando el operador canceló. Se consulta ENTRE guías, nunca a mitad de una. */
  isCancelled: () => boolean
  /** `false` cuando el componente se desmontó: el bucle para y no toca más estado. */
  isMounted: () => boolean
  /** Progreso para el modal. `index` es 0-based. */
  onProgress?: (index: number, total: number, shipmentNumber: string) => void
}

/**
 * Imprime una tanda Zebra guía por guía y devuelve QUÉ pasó con cada una.
 *
 * El bucle de consulta (`ConsultShipmentsV2.printLabelApi`) ignora el resultado de cada
 * impresión: `PrintLabel` devuelve `false` en vez de lanzar, así que 12 fallos de 40
 * terminan mostrando "Impresión completada". Acá se registra CADA guía y se devuelven los
 * números que fallaron, que es lo único con lo que el operador puede reimprimir.
 *
 * Secuencial a propósito: hay una sola impresora y las etiquetas tienen que salir en el
 * orden que el operador ve en pantalla.
 */
export const runZebraPrint = async (
  rows: BulkPrintRow[],
  deps: ZebraRunDeps
): Promise<ZebraRunResult> => {
  const printed: string[] = []
  const failures: ZebraFailure[] = []

  for (let index = 0; index < rows.length; index += 1) {
    // Las dos guardas van ANTES de cada guía: cancelar a mitad de una impresión dejaría
    // la etiqueta a medio mandar al agente.
    if (!deps.isMounted())
      return { printed, failures, aborted: true, abortReason: 'unmounted' }
    if (deps.isCancelled())
      return { printed, failures, aborted: true, abortReason: 'cancelled' }

    const row = rows[index]
    deps.onProgress?.(index, rows.length, row.shipmentNumber)

    const label = await deps.fetchLabel(row.shipmentNumber)
    if (!label.ok || !label.stringLabel?.length) {
      failures.push({
        shipmentNumber: row.shipmentNumber,
        reason: 'label-fetch',
        message: label.message || 'No se pudo obtener la etiqueta.',
      })
      continue
    }

    const sent = await deps.sendToPrinter(
      label.stringLabel,
      label.labelFormat || ''
    )
    if (sent.ok) {
      printed.push(row.shipmentNumber)
      continue
    }

    failures.push({
      shipmentNumber: row.shipmentNumber,
      reason: sent.reason ?? 'print-failed',
      message: sent.message || 'No se pudo imprimir la etiqueta.',
    })

    // El agente de impresión no está corriendo: las que faltan van a fallar igual.
    // Seguir sería hacer esperar al operador por 99 fallos garantizados.
    if (sent.reason === 'agent-down')
      return { printed, failures, aborted: true, abortReason: 'agent-down' }
  }

  return { printed, failures, aborted: false }
}
